import React, { type JSX } from 'react';
import { CustomModal, CustomTransComponent, Hierarchy, renderGroupHierarchyIcons } from '../../../components';
import { Stack } from '@mui/material';
import { ModalActions } from '../../../components/CustomModal';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { RealmHierarchySkeleton } from '../../RealmManagement/Skeletons/RealmHierarchySkeleton';
import {
    setSelectedPreviewNode,
    setWidgetDevice,
    togglePreviewNodeModal,
} from '@fiji/common/src/features/widgetManagement/widgetConfigurationSlice';
import { useChannelHierarchyHandlers, useWidgetConfigHandlers } from '../common';
import { InputWithCustomPlaceholder } from 'components/InputWithCustomPlaceholder';
import { useSearchDeviceOrGroup } from '@fiji/common/src/hooks';
import { getWidgetTypeValidationConfig } from '@fiji/common/src/utils/helpers';
import { useTranslation } from 'react-i18next';

const DEVICE_FILTER_WIDGET_TYPES = [
    'group_list',
    'map',
    'device_list',
    'score',
    'components',
    'energy_flow',
    'metrics',
    'consumption_breakdown',
    'load_trends',
    'weather',
    'aggregated_trends',
];

const SKIP_FILTER_WIDGET_TYPES = ['timeline', 'schedule', 'upcoming'];

const getTypeFilterValue = (widgetTypeId: string | undefined): string =>
    DEVICE_FILTER_WIDGET_TYPES.includes(widgetTypeId ?? '') ? 'DEVICE' : 'GROUP';

const buildHierarchyFilters = (widgetTypeId: string | undefined, selectedSourceId: string | undefined): any[] => {
    const filters: any[] = [
        {
            key: 'type',
            operator: '!==',
            value: getTypeFilterValue(widgetTypeId),
            action: 'disableCheck',
        },
        {
            key: 'type',
            operator: '!==',
            value: widgetTypeId === 'weather' && 'GATEWAY',
            action: 'disableCheck',
        },
    ];

    if (selectedSourceId) {
        filters.push({
            key: 'deviceTypeId',
            operator: '===',
            value: selectedSourceId,
            action: 'disableCheck',
        });
    }

    return filters;
};

const shouldApplyHierarchyFilters = (widgetTypeId: string | undefined): boolean =>
    !SKIP_FILTER_WIDGET_TYPES.includes(widgetTypeId ?? '');

const getHeaderNodeLabel = (widgetTypeId: string | undefined): string =>
    getWidgetTypeValidationConfig(widgetTypeId ?? '')?.isSourceRequired ? 'Device' : 'Node';

const getHierarchyData = (filteredRecords: any, selectedOrg: any, groupsData: any, searchKey: any): any => {
    const hasSearchKey = Boolean(searchKey?.length);
    if (Array.isArray(filteredRecords)) {
        if (filteredRecords.length) {
            return filteredRecords;
        }

        if (hasSearchKey) {
            return [];
        }
    }

    if (!filteredRecords && hasSearchKey) {
        return [];
    }

    if (!filteredRecords && selectedOrg?.defaultGroupAccess) {
        return [
            {
                id: selectedOrg?.id,
                name: `${selectedOrg?.name} (Organization Root)`,
                icon: { web: { family: 'material-ui', name: 'CorporateFare' } },
                groupCount: selectedOrg?.groupCount,
                deviceCount: selectedOrg?.deviceCount,
                type: 'GROUP',
            },
        ];
    }

    return groupsData;
};

const getModalActions = (dispatch: any, selectedPreviewNode: any): ModalActions[] => [
    {
        key: '#cancel',
        label: <CustomTransComponent translationKey={'COMMON:CANCEL'} />,
        variant: 'outlined',
        close: true,
        handleClick: (): void => {
            dispatch(setSelectedPreviewNode(undefined));
            dispatch(togglePreviewNodeModal(false));
        },
    },
    {
        key: '#save',
        label: <CustomTransComponent translationKey={'COMMON:SAVE_LABEL'} />,
        variant: 'contained',
        handleClick: (): void => {
            dispatch(setWidgetDevice({ data: selectedPreviewNode, reset: true }));
            dispatch(setSelectedPreviewNode(undefined));
            dispatch(togglePreviewNodeModal(false));
        },
    },
];

export const PreviewDeviceModal = (): JSX.Element => {
    const selectedDeviceNode = useTypedSelector((state) => state.widgetConfiguration.device);
    const selectedPreviewNode = useTypedSelector((state) => state.widgetConfiguration?.node);
    const selectedSource = useTypedSelector((state) => state.widgetConfiguration.source);
    const isModalVisible = useTypedSelector((state) => state.widgetConfiguration.previewNodeModal['isVisible']);
    const selectedOrg = useTypedSelector((state) => state.org.selectedOrg);
    const dispatch = useAppDispatch();
    const [searchKey, setSearchKey] = React.useState<any>(null);
    const { t } = useTranslation();

    const {
        details: { widgetDetails: details },
    } = useWidgetConfigHandlers();

    const [controlledExpandedNodes, setControlledExpandedNodes] = React.useState<string[]>([]);
    const { currentData: filteredRecords, isLoading } = useSearchDeviceOrGroup({ searchKey });
    const widgetTypeId = details?.widgetType?.id;
    const filters = buildHierarchyFilters(widgetTypeId, selectedSource?.id);

    const {
        groupsData,
        handleCleanHierarchyNode,
        handleFetchHierarchy,
        hierarchyMappingData,
        hasChildNode,
        loadingHierarchyNode,
    } = useChannelHierarchyHandlers({ widgetType: widgetTypeId });

    const actions = getModalActions(dispatch, selectedPreviewNode);
    const headerNodeLabel = getHeaderNodeLabel(widgetTypeId);
    const hierarchyData = getHierarchyData(filteredRecords, selectedOrg, groupsData, searchKey);
    const selectedNodeId = selectedPreviewNode?.id ?? selectedDeviceNode?.id;
    const hierarchyFilters = shouldApplyHierarchyFilters(widgetTypeId) ? filters : undefined;

    const handleNodeClick = (device: any): void => {
        dispatch(setSelectedPreviewNode(device));
    };

    return (
        <CustomModal
            isOpen={isModalVisible}
            isLoading={false}
            type="primary"
            actions={actions}
            header={
                <CustomTransComponent
                    translationKey={'WIDGETS:PREVIEW_MODAL.HEADER'}
                    replace={{
                        replace: {
                            header: headerNodeLabel,
                        },
                    }}
                />
            }
        >
            <InputWithCustomPlaceholder
                placeholder={t('COMMON:SEARCH_PLACEHOLDER')}
                inputProps={{ 'aria-label': 'search' }}
                value={searchKey ?? ''}
                onChange={(e: any) => {
                    setSearchKey(e.target.value);
                }}
            />
            <Stack className="height-432 overflow-y-scroll" mt={1}>
                <Hierarchy
                    key="#12e1de2r42f"
                    labelKey="name"
                    data={hierarchyData}
                    loadMore={handleFetchHierarchy}
                    hierarchyMappingData={hierarchyMappingData}
                    cleanHiearchyNode={handleCleanHierarchyNode}
                    loadingNode={loadingHierarchyNode}
                    controlledExpandedNodes={controlledExpandedNodes}
                    setControlledExpandedNodes={setControlledExpandedNodes}
                    selectionType="radio"
                    isLoading={!groupsData || isLoading}
                    handleTreeNodeSelection={handleNodeClick}
                    loadingSkeleton={<RealmHierarchySkeleton row={3} />}
                    hasChild={hasChildNode}
                    selectedNodes={selectedNodeId}
                    getAvatarChild={renderGroupHierarchyIcons}
                    filters={hierarchyFilters}
                />
            </Stack>
        </CustomModal>
    );
};
